import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { RichTextContent } from '@/components/RichTextContent';
import { SubgridEmptyState } from '@/components/SubgridEmptyState';
import {
  getClient,
  deleteClientContact,
  deleteClientNote,
  getClientContacts,
  getClientNotes,
  getClientTimeline,
  getClientServiceLocations,
  updateClient,
} from '@/features/clients/api/clients';
import type { ClientContactSummary, ClientNoteSummary } from '@/features/clients/types/Client';

export function ClientDetailsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clientId = Number(useParams().clientId);
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState<{ name: string; billingAddress: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [deletingContact, setDeletingContact] = useState<ClientContactSummary | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [deletingNote, setDeletingNote] = useState<ClientNoteSummary | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
  const results = useQueries({
    queries: [
      {
        queryKey: ['clients', clientId],
        queryFn: () => getClient(clientId),
        enabled: clientId > 0,
      },
      {
        queryKey: ['clients', clientId, 'contacts'],
        queryFn: () => getClientContacts(clientId),
        enabled: clientId > 0,
      },
      {
        queryKey: ['clients', clientId, 'locations'],
        queryFn: () => getClientServiceLocations(clientId),
        enabled: clientId > 0,
      },
      {
        queryKey: ['clients', clientId, 'notes'],
        queryFn: () => getClientNotes(clientId),
        enabled: clientId > 0,
      },
      {
        queryKey: ['clients', clientId, 'timeline'],
        queryFn: () => getClientTimeline(clientId),
        enabled: clientId > 0,
      },
    ],
  });
  const [clientQuery, contactsQuery, locationsQuery, notesQuery, timelineQuery] = results;
  const loading = clientQuery.isLoading;
  const failed = !Number.isInteger(clientId) || clientId <= 0 || clientQuery.isError;
  const refresh = () => void Promise.all(results.map((result) => result.refetch()));
  const draftName = draft?.name ?? clientQuery.data?.name ?? '';
  const draftBillingAddress = draft?.billingAddress ?? clientQuery.data?.billingAddress ?? '';
  const saveMutation = useMutation({
    mutationFn: () =>
      updateClient(clientId, {
        name: draftName.trim(),
        billingAddress: draftBillingAddress.trim() || null,
      }),
    onSuccess: async () => {
      setDirty(false);
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });
  const deleteContactMutation = useMutation({
    mutationFn: (contactId: number) => deleteClientContact(clientId, contactId),
    onSuccess: async () => {
      setDeletingContact(null);
      setSelectedContactId(null);
      await queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'contacts'] });
    },
  });
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: number) => deleteClientNote(clientId, noteId),
    onSuccess: async () => {
      setDeletingNote(null);
      setSelectedNoteId(null);
      await queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'notes'] });
    },
  });

  if (loading)
    return (
      <Stack alignItems="center" sx={{ p: 8 }}>
        <CircularProgress aria-label="Loading client" />
      </Stack>
    );
  if (failed || !clientQuery.data)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This client record could not be loaded.
      </Alert>
    );

  const client = clientQuery.data;
  const contacts = contactsQuery.data ?? [];
  const selectedContact = contacts.find((contact) => contact.id === selectedContactId) ?? null;
  const locations = locationsQuery.data ?? [];
  const notes = notesQuery.data ?? [];
  const timeline = timelineQuery.data ?? [];
  const relatedDataFailed =
    contactsQuery.isError || locationsQuery.isError || notesQuery.isError || timelineQuery.isError;

  return (
    <Box sx={{ p: 1 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          boxShadow: '0 2px 6px rgba(0,0,0,.14)',
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ minHeight: 44, px: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => void navigate('/clients')}>
            Back
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={refresh}>
            Refresh
          </Button>
          <Button disabled={!dirty || !draftName.trim()} onClick={() => saveMutation.mutate()}>
            Save
          </Button>
        </Stack>
      </Paper>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          boxShadow: '0 2px 6px rgba(0,0,0,.14)',
        }}
      >
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: '#d2d0ce', color: '#323130' }}>{initials(draftName)}</Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h1">
                {draftName}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  - {dirty ? 'Unsaved' : 'Saved'}
                </Typography>
              </Typography>
              <Typography variant="body2">Client</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography color="primary" fontWeight={600}>
                PoolAppy Manager
              </Typography>
              <Typography variant="caption">Owner</Typography>
            </Box>
          </Stack>
          <Tabs value={tab} onChange={(_, value: number) => setTab(value)} sx={{ mt: 1.5 }}>
            <Tab label="Summary" />
            <Tab label={`Contacts (${contacts.length})`} />
            <Tab label={`Service Locations (${locations.length})`} />
            <Tab label={`Notes (${notes.length})`} />
          </Tabs>
        </Box>
      </Paper>
      <Box sx={{ minHeight: 300 }}>
        {relatedDataFailed && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Some related information could not be loaded. The client record is still available; use
            Refresh to try again.
          </Alert>
        )}
        {tab === 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(340px, .9fr) minmax(360px, 1.1fr)' },
              gap: 1.5,
            }}
          >
            <Section title="CLIENT INFORMATION">
              <EditableField
                label="Client name"
                value={draftName}
                onChange={(name) => {
                  setDraft({ name, billingAddress: draftBillingAddress });
                  setDirty(true);
                }}
              />
              <EditableField
                label="Billing address"
                value={draftBillingAddress}
                multiline
                onChange={(billingAddress) => {
                  setDraft({ name: draftName, billingAddress });
                  setDirty(true);
                }}
              />
              <Field label="Created" value={formatDate(client.createdAt)} />
              {contacts[0] && (
                <>
                  <Field label="Primary contact" value={contacts[0].name} />
                  <Field
                    label="Email"
                    value={contacts[0].email ?? 'Not provided'}
                    action={{
                      label: 'Email contact',
                      href: contacts[0].email ? `mailto:${contacts[0].email}` : undefined,
                      icon: <EmailOutlinedIcon />,
                    }}
                  />
                  <Field
                    label="Mobile"
                    value={contacts[0].phone ?? 'Not provided'}
                    action={{
                      label: 'Call mobile',
                      href: contacts[0].phone ? `tel:${contacts[0].phone}` : undefined,
                      icon: <PhoneOutlinedIcon />,
                    }}
                  />
                  <Field
                    label="Other"
                    value={contacts[0].homePhone ?? 'Not provided'}
                    action={{
                      label: 'Call other number',
                      href: contacts[0].homePhone ? `tel:${contacts[0].homePhone}` : undefined,
                      icon: <PhoneOutlinedIcon />,
                    }}
                  />
                </>
              )}
            </Section>
            <Stack spacing={1.5}>
              <Section title="Up next">
                <Typography fontWeight={600}>
                  {locations.length
                    ? `${locations.length} service location${locations.length === 1 ? '' : 's'} available`
                    : 'No service locations scheduled'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Open Service Locations to review servicing information.
                </Typography>
              </Section>
              <Section title="Timeline">
                <Typography variant="body2" color="text.secondary" sx={{ pb: 1 }}>
                  Recent client activity
                </Typography>
                {timeline.length ? (
                  timeline.slice(0, 8).map((entry) => (
                    <Box key={entry.id} sx={{ py: 1.25, borderTop: '1px solid #e1dfdd' }}>
                      <RichTextContent value={entry.note ?? 'Activity recorded'} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(entry.created_at)}
                        {entry.type ? ` · ${entry.type}` : ''}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="text.secondary">No timeline activity to show.</Typography>
                )}
              </Section>
            </Stack>
          </Box>
        )}
        {tab === 1 && (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: '0 2px 6px rgba(0,0,0,.12)',
              p: 1.5,
            }}
          >
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={subgridCommandSx}
            >
              {selectedContact ? (
                <>
                  <Button
                    startIcon={<EditOutlinedIcon />}
                    sx={commandButtonSx}
                    onClick={() =>
                      void navigate(`/clients/${clientId}/contacts/${selectedContact.id}`)
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    startIcon={<DeleteOutlineIcon />}
                    sx={deleteCommandButtonSx}
                    onClick={() => setDeletingContact(selectedContact)}
                  >
                    Delete
                  </Button>
                </>
              ) : (
                <Button
                  startIcon={<AddIcon />}
                  sx={commandButtonSx}
                  onClick={() => void navigate(`/clients/${clientId}/contacts/new`)}
                >
                  New Contact
                </Button>
              )}
            </Stack>
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: 760 }}>
                <Box sx={contactGridHeaderSx}>
                  <Box aria-hidden="true" />
                  <Typography fontWeight={600}>Name ↑</Typography>
                  <Typography fontWeight={600}>Email</Typography>
                  <Typography fontWeight={600}>Telephone</Typography>
                  <Typography fontWeight={600}>Notifications</Typography>
                </Box>
                {contacts.length ? (
                  contacts.map((contact) => {
                    const selected = selectedContactId === contact.id;
                    const toggleSelection = () =>
                      setSelectedContactId(selected ? null : contact.id);
                    return (
                      <Box
                        key={contact.id}
                        role="link"
                        tabIndex={0}
                        aria-label={`Open ${contact.name}`}
                        onClick={() =>
                          void navigate(`/clients/${clientId}/contacts/${contact.id}`)
                        }
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            void navigate(`/clients/${clientId}/contacts/${contact.id}`);
                          }
                        }}
                        sx={{
                          ...contactGridRowSx,
                          cursor: 'pointer',
                          bgcolor: selected ? '#e5f1fb' : 'transparent',
                          '&:hover': { bgcolor: selected ? '#d7eaf8' : '#f3f2f1' },
                          '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: -2,
                          },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selected}
                          inputProps={{ 'aria-label': `Select ${contact.name}` }}
                          onClick={(event) => event.stopPropagation()}
                          onChange={toggleSelection}
                        />
                        <Link
                          component="button"
                          type="button"
                          underline="none"
                          textAlign="left"
                          onClick={(event) => {
                            event.stopPropagation();
                            void navigate(`/clients/${clientId}/contacts/${contact.id}`);
                          }}
                        >
                          {contact.name}
                        </Link>
                        <Typography>{contact.email ?? '—'}</Typography>
                        <Typography>{contact.phone ?? contact.homePhone ?? '—'}</Typography>
                        <Typography>
                          {[contact.notifyByEmail && 'Email', contact.notifyBySms && 'SMS']
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <SubgridEmptyState message="We didn't find anything to show here" />
                )}
              </Box>
            </Box>
            <Typography variant="body2" sx={{ pt: 1.5 }}>
              Rows: {contacts.length}
            </Typography>
          </Paper>
        )}
        {tab === 2 && (
          <Paper elevation={0} sx={subgridPaperSx}>
            <Stack direction="row" justifyContent="flex-end" sx={subgridCommandSx}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => void navigate(`/clients/${clientId}/service-locations/new`)}
              >
                New Service Location
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(260px,1fr) minmax(180px,.7fr)',
                minWidth: 560,
              }}
            >
              {['Name', 'Gate / access code'].map((heading) => (
                <Typography key={heading} fontWeight={600} sx={subgridCellSx}>
                  {heading}
                </Typography>
              ))}
              {locations.map((location) => (
                <Box
                  key={location.id}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open ${location.name}`}
                  onClick={() =>
                    void navigate(`/clients/${clientId}/service-locations/${location.id}`)
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void navigate(`/clients/${clientId}/service-locations/${location.id}`);
                    }
                  }}
                  sx={{
                    display: 'grid',
                    gridColumn: '1 / -1',
                    gridTemplateColumns: 'subgrid',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f3f2f1' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Box sx={subgridCellSx}>
                    <Link
                      component={RouterLink}
                      to={`/clients/${clientId}/service-locations/${location.id}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {location.name}
                    </Link>
                  </Box>
                  <Typography sx={subgridCellSx}>{location.gateCode ?? '—'}</Typography>
                </Box>
              ))}
            </Box>
            {!locations.length && (
              <SubgridEmptyState message="We didn't find anything to show here" />
            )}
            <Typography variant="body2" sx={{ pt: 1.5 }}>
              Rows: {locations.length}
            </Typography>
          </Paper>
        )}
        {tab === 3 && (
          <Paper elevation={0} sx={subgridPaperSx}>
            <Stack direction="row" justifyContent="flex-end" sx={subgridCommandSx}>
              {selectedNoteId === null ? (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => void navigate(`/clients/${clientId}/notes/new`)}
                >
                  New Note
                </Button>
              ) : (
                <>
                  <Button
                    startIcon={<EditOutlinedIcon />}
                    onClick={() =>
                      void navigate(`/clients/${clientId}/notes/${selectedNoteId}`)
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => {
                      const note = notes.find((item) => item.id === selectedNoteId);
                      if (note) setDeletingNote(note);
                    }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '48px minmax(280px, 1fr) 220px',
                minWidth: 700,
              }}
            >
              {['', 'Note', 'Created by'].map((heading, index) => (
                <Typography key={heading} fontWeight={600} sx={subgridCellSx}>
                  {index === 0 ? null : heading}
                </Typography>
              ))}
              {notes.map((note) => {
                const selected = selectedNoteId === note.id;
                const toggleSelection = () => setSelectedNoteId(selected ? null : note.id);
                return (
                  <Box
                    key={note.id}
                  role="link"
                  tabIndex={0}
                  aria-label="Open note"
                  onClick={() => void navigate(`/clients/${clientId}/notes/${note.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void navigate(`/clients/${clientId}/notes/${note.id}`);
                    }
                  }}
                  sx={{
                    display: 'grid',
                    gridColumn: '1 / -1',
                    gridTemplateColumns: 'subgrid',
                    cursor: 'pointer',
                    bgcolor: selected ? '#e5f1fb' : 'transparent',
                    '&:hover': { bgcolor: selected ? '#d7eaf8' : '#f3f2f1' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selected}
                    inputProps={{ 'aria-label': 'Select note' }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={toggleSelection}
                    sx={subgridCellSx}
                  />
                  <Box sx={subgridCellSx}>
                    <RichTextContent value={note.note} />
                  </Box>
                  <Typography sx={subgridCellSx}>
                    {note.author}
                    <br />
                    <Typography component="span" variant="caption">
                      {formatDate(note.createdAt)}
                    </Typography>
                  </Typography>
                </Box>
                );
              })}
            </Box>
            {!notes.length && <SubgridEmptyState message="We didn't find anything to show here" />}
            <Typography variant="body2" sx={{ pt: 1.5 }}>
              Rows: {notes.length}
              {selectedNoteId !== null ? ' � Selected: 1' : ''}
            </Typography>
          </Paper>
        )}
      </Box>
      <Dialog open={deletingContact !== null} onClose={() => setDeletingContact(null)}>
        <DialogTitle>Delete contact?</DialogTitle>
        <DialogContent>This permanently deletes {deletingContact?.name}.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingContact(null)}>Cancel</Button>
          <Button
            color="error"
            disabled={deleteContactMutation.isPending}
            onClick={() => deletingContact && deleteContactMutation.mutate(deletingContact.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deletingNote !== null} onClose={() => setDeletingNote(null)}>
        <DialogTitle>Delete note?</DialogTitle>
        <DialogContent>This permanently deletes the selected note.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingNote(null)}>Cancel</Button>
          <Button
            color="error"
            disabled={deleteNoteMutation.isPending}
            onClick={() => deletingNote && deleteNoteMutation.mutate(deletingNote.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const contactGridColumns = '42px minmax(150px, 1.1fr) minmax(190px, 1.2fr) 150px 130px';
const contactGridHeaderSx = {
  display: 'grid',
  gridTemplateColumns: contactGridColumns,
  alignItems: 'center',
  minHeight: 44,
  borderBottom: '1px solid #e1dfdd',
  gap: 1,
};
const contactGridRowSx = {
  display: 'grid',
  gridTemplateColumns: contactGridColumns,
  alignItems: 'center',
  minHeight: 48,
  borderBottom: '1px solid #edebe9',
  gap: 1,
};
const subgridPaperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
  p: 1.5,
  overflowX: 'auto',
};
const subgridCommandSx = { minHeight: 44, borderBottom: '1px solid #e1dfdd' };
const subgridCellSx = { minHeight: 46, px: 1.5, py: 1.25, borderBottom: '1px solid #edebe9' };
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const deleteCommandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'error.main' },
};

function Field({
  label,
  value,
  action,
}: {
  label: string;
  value: string;
  action?: { label: string; href?: string; icon: React.ReactNode };
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        sx={{
          bgcolor: '#f3f2f1',
          minHeight: 34,
          pl: 1.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ flex: 1, py: 0.8 }}>{value}</Typography>
        {action && (
          <Button
            component="a"
            href={action.href}
            disabled={!action.href}
            aria-label={action.label}
            sx={{ minWidth: 38, alignSelf: 'stretch', color: 'text.secondary' }}
          >
            {action.icon}
          </Button>
        )}
      </Box>
    </Box>
  );
}
function EditableField({
  label,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <TextField
        fullWidth
        variant="standard"
        value={value}
        multiline={multiline}
        onChange={(event) => onChange(event.target.value)}
        sx={{ bgcolor: '#f3f2f1', '& .MuiInputBase-root': { px: 1.5, py: 0.4 } }}
      />
    </Box>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: '0 2px 6px rgba(0,0,0,.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}
function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(date);
}
function initials(value: string) {
  return value
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
